import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Typography, Paper, Container, CircularProgress, Alert,
  Grid, Avatar, Divider, CardHeader, Tabs, Tab, Button
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EditIcon from '@mui/icons-material/Edit';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Import the new form components
import MeeEvaluationForm from './components/MeeEvaluationForm';
import MptEvaluationForm from './components/MptEvaluationForm';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const PointSheetViewer = ({ url }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const containerRef = useRef(null);
  const [viewerWidth, setViewerWidth] = useState(500);

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        // Subtract padding/margins for better fit
        setViewerWidth(containerRef.current.offsetWidth - 40); 
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  if (!url) {
     return (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', border: '2px dashed #ccc', backgroundColor: '#fafafa' }}>
            <Alert severity="info" sx={{mb: 2, display: 'inline-flex'}}>Point Sheet 정보를 찾을 수 없습니다.</Alert>
            <Typography variant="body2" color="text.secondary">
                답안에 질문 ID가 연결되지 않았거나, <br/>
                해당 질문에 Point Sheet PDF 링크가 등록되지 않았습니다.
            </Typography>
        </Paper>
     )
  }

  return (
    <Box ref={containerRef}>
        <Box sx={{ minHeight: '60vh', overflowY: 'auto', border: '1px solid #ddd', borderRadius: 1, mb: 1, display: 'flex', justifyContent: 'center', background: '#f5f5f5' }}>
            <Document file={url} onLoadSuccess={onDocumentLoadSuccess} onLoadError={(error) => console.error('PDF 로딩 실패:', error)}>
                <Page pageNumber={pageNumber} width={viewerWidth} />
            </Document>
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center" gap={2} mt={2}>
            <Button size="small" variant="outlined" onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1}>
                이전 페이지
            </Button>
            <Typography variant="body2">
                {pageNumber} / {numPages || '?'}
            </Typography>
            <Button size="small" variant="outlined" onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} disabled={!numPages || pageNumber >= numPages}>
                다음 페이지
            </Button>
        </Box>
    </Box>
  );
};

const EvaluationDetailPage = () => {
  const { answerType, answerId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  
  const [answer, setAnswer] = useState(null);
  const [pointSheetUrl, setPointSheetUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);

  // The evaluation mode is derived directly from the URL parameter.
  const evaluationMode = answerType?.toUpperCase();

  useEffect(() => {
    if (!answerId || !answerType) return;

    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      
      const isMee = answerType.toLowerCase() === 'mee';
      const answerTableName = isMee ? 'mee_answers' : 'mpt_answers';
      const questionTableName = isMee ? 'mee_paper_sets' : 'mpt_paper_sets';
      const foreignKey = isMee ? 'question_id' : 'set_id'; // This might need adjustment for MPT
      
      const selectQuery = isMee
        ? `*,
          profiles(name, email),
          mee_paper_sets!fk_mee_paper_sets(
            id,
            a_url,
            mee_year_month_set_id,
            mee_year_month_sets(year, month)
          )`
        : `*,
          profiles(name, email),
          mpt_paper_sets!question_id(
            id,
            pointsheet_url,
            mpt_year_month_set_id,
            mpt_year_month_sets(year, month)
          )`;

      try {
        const { data: answerData, error: answerError } = await supabase
          .from(answerTableName)
          .select(selectQuery)
          .eq('id', answerId)
          .single();

        if (answerError) throw answerError;
        setAnswer(answerData);

        // Extract pointsheet URL from the fetched data
        let url = null;
        if (answerData) {
          if (isMee && answerData.mee_paper_sets) {
            url = answerData.mee_paper_sets.a_url;
          } else if (!isMee && answerData.mpt_paper_sets) {
            url = answerData.mpt_paper_sets.pointsheet_url;
          }
        }
        setPointSheetUrl(url);

      } catch (err) {
        console.error('데이터 로딩 중 오류 발생:', err);
        setError(`정보를 불러오는 중 오류가 발생했습니다: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [answerId, answerType]);

  const handleSubmit = async (evaluationData) => {
    setIsSubmitting(true);
    setError(null);

    const evaluationTable = answerType.toLowerCase() === 'mee' ? 'mee_evaluations' : 'mpt_evaluations';
    const answerTable = answerType.toLowerCase() === 'mee' ? 'mee_answers' : 'mpt_answers';

    const finalEvaluationData = {
      answer_id: answerId,
      evaluator_id: user.id,
      ...evaluationData, // Spread the data from the specific form (MEE or MPT)
      evaluation_type: answerType.toUpperCase(),
      evaluation_status: '평가 완료',
      evaluated_at: new Date().toISOString(),
    };

    try {
      const { data: insertedEvaluation, error: insertError } = await supabase
        .from(evaluationTable)
        .insert(finalEvaluationData)
        .select()
        .single();

      if (insertError) throw insertError;

      const { error: updateError } = await supabase
        .from(answerTable)
        .update({ 
          evaluation_status: '평가 완료',
          evaluator_id: user.id,
        })
        .eq('id', answerId);
      
      if (updateError) {
        await supabase.from(evaluationTable).delete().eq('id', insertedEvaluation.id);
        throw updateError;
      }
      
      alert('평가가 성공적으로 저장되었습니다.');
      navigate('/evaluator');

    } catch (err) {
      console.error('평가 저장 중 오류 발생:', err);
      setError(`평가 저장에 실패했습니다: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <Container sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Container>;
  if (error) return <Container sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  if (!answer) return <Container sx={{ py: 4 }}><Alert severity="warning">답안을 찾을 수 없습니다.</Alert></Container>;

  const getYearMonth = (ans) => {
    if (evaluationMode === 'MEE' && ans.mee_paper_sets?.mee_year_month_sets) {
      const { year, month } = ans.mee_paper_sets.mee_year_month_sets;
      return `${year} ${month}`;
    }
    if (evaluationMode === 'MPT' && ans.mpt_paper_sets?.mpt_year_month_sets) {
      const { year, month } = ans.mpt_paper_sets.mpt_year_month_sets;
      return `${year} ${month}`;
    }
    return '';
  };

  const subjectInfo = evaluationMode === 'MEE'
    ? `${answer.subject || 'N/A'} (${getYearMonth(answer)})`.trim()
    : `${answer.set_title || 'N/A'} (${getYearMonth(answer)})`.trim();

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: { xs: 2, sm: 4, md: 6 } }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          {evaluationMode} 답안 평가
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          {`과목/세트: ${subjectInfo}`}
        </Typography>
      </Box>
      
      <Grid container spacing={4}>
        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: { xs: 2, md: 3 }, height: { lg: 'calc(100vh - 220px)' }, display: 'flex', flexDirection: 'column', borderRadius: '12px' }}>
            <CardHeader
              avatar={<Avatar sx={{ bgcolor: 'primary.main' }}><AccountCircleIcon /></Avatar>}
              titleTypographyProps={{ fontWeight: 'bold' }}
              title={`작성자: ${answer.profiles?.name || '이름 없음'}`}
              subheader={`이메일: ${answer.profiles?.email || '이메일 없음'}`}
            />
            <Divider sx={{ my: 1 }} />
            <Box 
              className="prose"
              sx={{ 
                flex: 1,
                overflowY: 'auto',
                wordBreak: 'break-word',
                p: { xs: 1, md: 2 }
              }}
              dangerouslySetInnerHTML={{ __html: answer.answer_content }} 
            />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: '12px' }}>
            <CardHeader
              avatar={<Avatar sx={{ bgcolor: 'secondary.main' }}><EditIcon /></Avatar>}
              titleTypographyProps={{ fontWeight: 'bold' }}
              title={`평가자: ${userProfile?.name || '이름 없음'}`}
              subheader={`이메일: ${userProfile?.email || '이메일 없음'}`}
            />
            <Divider sx={{ my: 2 }}/>
            
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} sx={{ mb: 2 }}>
              <Tab label="쟁점별 평가" />
              <Tab label="NCBE Point Sheet" />
            </Tabs>

            {currentTab === 0 && (
              <Box>
                {evaluationMode === 'MEE' && (
                  <MeeEvaluationForm 
                    isSubmitting={isSubmitting} 
                    onEvaluationSubmit={handleSubmit}
                  />
                )}
                {evaluationMode === 'MPT' && (
                  <MptEvaluationForm 
                    isSubmitting={isSubmitting} 
                    onEvaluationSubmit={handleSubmit}
                  />
                )}
                 {!['MEE', 'MPT'].includes(evaluationMode) && (
                  <Alert severity="warning">알 수 없는 평가 타입입니다: {evaluationMode}</Alert>
                )}
              </Box>
            )}

            {currentTab === 1 && (
              <PointSheetViewer url={pointSheetUrl} />
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default EvaluationDetailPage;
